import { sessionFetch } from './session-fetch';
import type {
  HostingDeclaredSiteInput,
  HostingDeclaredSiteView,
  HostingSelfEnvironment,
  HostingSiteCatalogPage,
} from '@tosho/contracts';
import type { HostingPleskDomainQuery } from './hosting-plesk-inventory-api';
export class HostingSiteRequestError extends Error {
  public constructor(public readonly code: string) {
    super('Site indisponible.');
  }
}
async function request<T>(path: string, method = 'GET', csrf?: string, body?: unknown): Promise<T> {
  const response = await sessionFetch('/api/' + path, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    signal: AbortSignal.timeout(12000),
    headers: {
      ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const data: unknown = await response.json().catch(() => null);
    throw new HostingSiteRequestError(
      data && typeof data === 'object' && 'code' in data && typeof data.code === 'string'
        ? data.code
        : 'UNAVAILABLE',
    );
  }
  return response.json() as Promise<T>;
}
export const hostingSitesApi = {
  list(query: HostingPleskDomainQuery): Promise<HostingSiteCatalogPage> {
    const params = new URLSearchParams({
      search: query.search,
      client: query.client,
      owner: query.owner ?? 'ALL',
      presence: query.presence,
      domainType: query.domainType ?? 'ALL',
      environment: query.environment ?? 'ALL',
      page: String(query.page),
      pageSize: String(query.pageSize),
    });
    if (query.serverId) params.set('serverId', query.serverId);
    return request('hosting-sites?' + params.toString());
  },
  self: (): Promise<HostingSelfEnvironment> => request('hosting-declared-sites/self-environment'),
  save: (
    site: HostingDeclaredSiteView | null,
    input: HostingDeclaredSiteInput,
    csrf: string,
  ): Promise<{ saved: true; id: string; revision: number }> =>
    request(
      'hosting-declared-sites' + (site ? '/' + encodeURIComponent(site.id) : ''),
      site ? 'PATCH' : 'POST',
      csrf,
      { ...input, ...(site ? { expectedRevision: site.revision } : {}) },
    ),
};
export function hostingSiteError(error: unknown, operation: 'READ' | 'WRITE' = 'WRITE'): string {
  if (error instanceof HostingSiteRequestError) {
    if (error.code === 'HOSTING_SITE_RATE_LIMITED')
      return 'Trop de demandes rapprochées. Patientez un instant avant de réessayer.';
    if (error.code === 'STALE_RECORD')
      return 'Cette fiche a changé. Vos saisies sont conservées ; fermez et actualisez avant de les enregistrer.';
    if (error.code === 'SITE_ALREADY_REFERENCED')
      return 'Ce domaine est déjà référencé sur ce serveur. Retrouvez sa fiche dans la liste.';
    if (error.code === 'HOSTING_SITE_INVALID')
      return 'Vérifiez l’adresse du site, le serveur et les informations saisies.';
    if (error.code === 'HOSTING_SITE_UNAVAILABLE')
      return 'Le serveur ou le Client choisi n’est plus disponible. Les sites Plesk doivent être récupérés depuis leur inventaire.';
  }
  return operation === 'READ'
    ? 'La liste des sites n’a pas pu être chargée. Réessayez dans un instant.'
    : 'Le site n’a pas pu être enregistré. Réessayez dans un instant.';
}
